import { Link, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { Phone, User } from "lucide-react";
import { useId, useState, type FormEvent } from "react";
import { supabase } from "@/integrations/supabase/client";

import logoOfferto from "@/assets/logo-offerto.png";

import { diensten, plaatsen } from "../lib/seo-data";
import { useTaal } from "../lib/taal";
import { talen, type Sleutel } from "../lib/vertalingen";
import { Button } from "./ui/button";

export function Logo() {
  return (
    <Link
      to="/"
      aria-label="offerto"
      className="flex min-w-0 shrink-0 items-center"
    >
      <img
        src={logoOfferto}
        alt="offerto"
        width={1096}
        height={209}
        className="h-[26px] w-auto min-[360px]:h-[29px] md:h-[35px] xl:h-[42px]"
      />
    </Link>
  );
}

const nav = [
  { to: "/", sleutel: "navHome" },
  { to: "/diensten", sleutel: "navDiensten" },
  { to: "/plaatsen", sleutel: "navPlaatsen" },
  { to: "/nieuws", sleutel: "nieuwsTitel" },
  { to: "/wat-kost-een-elektricien", sleutel: "kostenTitel" },
  { to: "/hoe-het-werkt", sleutel: "navHoeHetWerkt" },
  { to: "/veelgestelde-vragen", sleutel: "navVeelgesteldeVragen" },
  { to: "/contact", sleutel: "navContact" },
] as const satisfies readonly { to: string; sleutel: Sleutel }[];

// Shared content width: matches the homepage hero edges (left padding edge to the right edge of the review card).
export const PAGE_CONTAINER = "mx-auto w-full max-w-[1440px] px-5 md:px-10 lg:px-6 xl:px-10";

export function Header() {
  const { data: ingelogd = false } = useQuery({
    queryKey: ["account-sessie"],
    queryFn: async () => { const { data } = await supabase.auth.getUser(); return Boolean(data.user); },
    staleTime: 60_000,
  });
  const { t } = useTaal();
  return (
    <header className="sticky top-0 z-50 border-b border-border bg-card">
      <div aria-hidden="true" className="h-2.5 bg-hero-strip-dark xl:h-[13px]" />
      <div aria-hidden="true" className="h-2.5 bg-hero-strip-blue xl:h-[13px]" />
      <div aria-hidden="true" className="h-2.5 bg-hero-accent xl:h-[12px]" />
      <div className={`${PAGE_CONTAINER} grid h-[80px] grid-cols-[minmax(0,1fr)_auto] items-center gap-3 md:h-[86px]`}>
        <div className="min-w-0 shrink-0">
          <Logo />
        </div>
        <div className="flex shrink-0 items-center gap-1 min-[360px]:gap-2">
          <nav className="hidden items-center gap-7 text-base font-medium xl:flex" aria-label="Contact en inloggen">
            <Link to="/contact" className="flex items-center gap-2 text-foreground transition-colors hover:text-primary">
              <Phone className="size-5" strokeWidth={2} aria-hidden="true" />
              {t("navContact")}
            </Link>
            <Link to={ingelogd ? "/account" : "/auth"} className="flex items-center gap-2 text-foreground transition-colors hover:text-primary">
              <User className="size-5" strokeWidth={2} aria-hidden="true" />
              {ingelogd ? "Mijn account" : t("navInloggen")}
            </Link>
          </nav>
        </div>
      </div>
    </header>
  );
}

export function Footer() {
  const { t } = useTaal();
  return (
    <footer className="mt-24 border-t border-border/60 bg-secondary">
      <div className={`${PAGE_CONTAINER} grid gap-8 py-12 sm:grid-cols-2 md:grid-cols-4 xl:grid-cols-5`}>
        <div className="md:col-span-2">
          <Logo />
          <p className="mt-3 max-w-sm text-sm text-muted-foreground">{t("footerIntro")}</p>
        </div>
        <div>
          <h3 className="text-sm font-semibold">{t("footerPaginas")}</h3>
          <ul className="mt-3 space-y-2 text-sm text-muted-foreground">
            {nav.map((i) => (
              <li key={i.to}>
                <Link to={i.to} className="hover:text-foreground">
                  {t(i.sleutel)}
                </Link>
              </li>
            ))}
            <li>
              <Link to="/aanmelden" className="hover:text-foreground">
                {t("navAanmelden")}
              </Link>
            </li>
            <li><Link to="/netbeheerders" className="hover:text-foreground">Netbeheerders</Link></li>
            <li><Link to="/privacy" className="hover:text-foreground">Privacy en cookies</Link></li>
            <li>
              <button type="button" onClick={() => window.dispatchEvent(new Event("open-cookie-settings"))} className="hover:text-foreground">
                Cookie-instellingen
              </button>
            </li>
          </ul>
        </div>
        <div>
          <h3 className="text-sm font-semibold">{t("footerKlussen")}</h3>
          <ul className="mt-3 space-y-2 text-sm text-muted-foreground">
            {diensten.slice(0, 5).map((d) => (
              <li key={d.slug}>
                <Link
                  to="/elektricien/$dienst"
                  params={{ dienst: d.slug }}
                  className="hover:text-foreground"
                >
                  {d.naam}
                </Link>
              </li>
            ))}
          </ul>
          <h3 className="mt-6 text-sm font-semibold">{t("footerPlaatsen")}</h3>
          <ul className="mt-3 space-y-2 text-sm text-muted-foreground">
            {plaatsen.slice(0, 5).map((p) => (
              <li key={p.slug}>
                <Link
                  to="/plaatsen/$plaats"
                  params={{ plaats: p.slug }}
                  className="hover:text-foreground"
                >
                  {t("footerElektricien")} {p.naam}
                </Link>
              </li>
            ))}
          </ul>
        </div>
        <nav aria-label={t("netwerkTitel")}>
          <h3 className="text-sm font-semibold">{t("netwerkTitel")}</h3>
          <ul className="mt-3 space-y-2 break-words text-sm text-muted-foreground">
            <li><a href="https://thuisbatterijenoffertes.com" target="_blank" rel="noopener" className="hover:text-foreground">ThuisbatterijenOffertes.com</a></li>
            <li><a href="https://warmtepompoffertes.com" target="_blank" rel="noopener" className="hover:text-foreground">WarmtepompOffertes.com</a></li>
            <li>BeveiligingOffertes.com</li>
            <li>GroepenkastOffertes.com</li>
            <li>SmarthomeOffertes.com</li>
            <li>ElektricienOffertes.com</li>
          </ul>
        </nav>
      </div>
      <div className="border-t border-border/60 py-5 text-center text-xs text-muted-foreground">
        © {new Date().getFullYear()} offerto
      </div>
    </footer>
  );
}

export function QuoteForm({ compact = false, hero = false, stacked = false }: { compact?: boolean; hero?: boolean; stacked?: boolean }) {
  const { t } = useTaal();
  const klusOpties = t("klusOpties");
  const klusLabels = t("klusLabels");
  const klusLabel = (optie: string) => klusLabels[optie] ?? optie;
  const [postcode, setPostcode] = useState("");
  const [huisnummer, setHuisnummer] = useState("");
  const [klus, setKlus] = useState("");
  const postcodeId = useId();
  const huisnummerId = useId();
  const klusId = useId();
  const navigate = useNavigate();

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (postcode.trim().length < 4) return;
    const klusIndex = klus === "" ? 0 : klusOpties.indexOf(klus);
    const hn = huisnummer.trim();
    void navigate({
      to: "/aanvraag",
      search: { postcode: postcode.trim(), klus: klusIndex, ...(hn ? { huisnummer: hn } : {}) },
    });
  }

  if (hero && stacked) {
    return <form onSubmit={onSubmit} className="grid w-full min-w-0 grid-cols-[minmax(0,1fr)_minmax(0,1fr)] gap-3">
      <label htmlFor={postcodeId} className="flex min-w-0 flex-col gap-1.5 text-sm font-semibold text-hero-muted">
        Postcode
        <input id={postcodeId} name="postcode" value={postcode} onChange={(e) => setPostcode(e.target.value)} placeholder="1234 AB" autoComplete="postal-code" required className="h-12 w-full min-w-0 rounded-md border border-hero-border bg-hero-card px-3 text-base text-hero-foreground outline-none placeholder:text-hero-placeholder focus-visible:ring-2 focus-visible:ring-hero-accent" />
      </label>
      <label htmlFor={huisnummerId} className="flex min-w-0 flex-col gap-1.5 text-sm font-semibold text-hero-muted">
        Huisnummer
        <input id={huisnummerId} name="huisnummer" value={huisnummer} onChange={(e) => setHuisnummer(e.target.value)} placeholder="12" inputMode="numeric" maxLength={7} className="h-12 w-full min-w-0 rounded-md border border-hero-border bg-hero-card px-3 text-base text-hero-foreground outline-none placeholder:text-hero-placeholder focus-visible:ring-2 focus-visible:ring-hero-accent" />
      </label>
      <label htmlFor={klusId} className="col-span-2 flex min-w-0 flex-col gap-1.5 text-sm font-semibold text-hero-muted">
        Werkzaamheden
        <select id={klusId} name="klus" value={klus === "" ? klusOpties[0] : klus} onChange={(e) => setKlus(e.target.value)} className="h-12 w-full min-w-0 rounded-md border border-hero-border bg-hero-card px-3 text-base text-hero-foreground outline-none focus-visible:ring-2 focus-visible:ring-hero-accent">
          {klusOpties.map((optie) => <option key={optie} value={optie}>{klusLabel(optie)}</option>)}
        </select>
      </label>
      <Button type="submit" variant="hero" className="col-span-2 mt-1 h-12 w-full min-w-0 rounded-md text-base font-bold">Ga verder</Button>
    </form>;
  }

  if (hero) {
    return (
      <form onSubmit={onSubmit} className="hero-quote-form grid grid-cols-[minmax(0,1fr)_90px] gap-2 rounded-[14px] border border-hero-border bg-hero-card p-2 shadow-hero-form md:max-w-full md:max-lg:w-fit md:max-lg:grid-cols-[116px_90px_minmax(0,max-content)_auto] lg:h-14 min-[1024px]:w-[500px] min-[1024px]:grid-cols-[100px_86px_minmax(0,1fr)_110px] min-[1150px]:w-[580px] min-[1150px]:grid-cols-[110px_100px_minmax(0,1fr)_125px] min-[1280px]:w-[610px] min-[1280px]:grid-cols-[112px_104px_minmax(0,1fr)_150px] lg:gap-0 lg:py-0 lg:pr-0 lg:overflow-hidden min-[1400px]:w-fit! min-[1400px]:grid-cols-[115px_115px_241px_180px]! min-[1400px]:h-16! min-[1400px]:gap-0 min-[1400px]:py-0 min-[1400px]:pr-0 min-[1400px]:overflow-hidden">
        <label htmlFor={postcodeId} className="flex min-w-0 flex-col gap-0.5 border-b border-hero-border px-3.5 py-2.5 md:border-b-0 md:border-r md:pt-1 md:pb-2">
          <span className="text-xs font-semibold text-hero-muted">Postcode</span>
          <input id={postcodeId} name="postcode" value={postcode} onChange={(e) => setPostcode(e.target.value)} placeholder="1234 AB" autoComplete="postal-code" required className="min-w-0 border-0 bg-transparent p-0 text-[18px] min-[1024px]:max-[1399px]:text-base text-hero-foreground outline-none placeholder:text-hero-placeholder focus-visible:ring-2 focus-visible:ring-hero-accent" />
        </label>
        <label htmlFor={huisnummerId} className="flex min-w-0 flex-col gap-0.5 border-b border-hero-border px-2 py-2.5 md:border-b-0 md:border-r md:pt-1 md:pb-2">
          <span className="text-xs font-semibold text-hero-muted">Huisnummer</span>
          <input id={huisnummerId} name="huisnummer" value={huisnummer} onChange={(e) => setHuisnummer(e.target.value)} placeholder="12" inputMode="numeric" maxLength={7} className="min-w-0 border-0 bg-transparent p-0 text-[18px] min-[1024px]:max-[1399px]:text-base text-hero-foreground outline-none placeholder:text-hero-placeholder focus-visible:ring-2 focus-visible:ring-hero-accent" />
        </label>
        <label htmlFor={klusId} className="col-span-2 flex min-w-0 flex-col gap-0.5 px-3.5 py-2.5 md:col-span-1 md:pt-1 md:pb-2">
          <span className="text-xs font-semibold text-hero-muted">Werkzaamheden</span>
          <select id={klusId} name="klus" value={klus === "" ? klusOpties[0] : klus} onChange={(e) => setKlus(e.target.value)} className="w-full min-w-0 border-0 bg-transparent p-0 text-[18px] min-[1024px]:max-[1399px]:text-base text-hero-foreground outline-none focus-visible:ring-2 focus-visible:ring-hero-accent">
            {klusOpties.map((optie) => <option key={optie} value={optie}>{klusLabel(optie)}</option>)}
          </select>
          <span aria-hidden="true" className="grid h-0 overflow-hidden whitespace-nowrap text-[18px] leading-none">
            {klusOpties.map((optie) => <span key={optie} className="justify-self-start">{klusLabel(optie)}</span>)}
          </span>
        </label>
        <Button type="submit" variant="hero" className="col-span-2 h-[50px] min-h-[50px] min-w-0 self-center rounded-[10px] px-5 text-base font-bold md:col-span-1 md:px-4 xl:px-5 lg:h-full! lg:min-h-0 lg:self-stretch lg:rounded-none lg:text-lg lg:font-extrabold xl:text-xl min-[1400px]:text-[24px]">Ga verder</Button>
      </form>
    );
  }

  return (
    <form
      onSubmit={onSubmit}
      className="w-full max-w-[616px] rounded-lg border border-border bg-card p-4 shadow-sm"
    >
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-[minmax(0,0.75fr)_minmax(0,1.25fr)_auto]">
        <input
          id={postcodeId}
          name="postcode"
          value={postcode}
          onChange={(e) => setPostcode(e.target.value)}
          placeholder={t("postcode")}
          aria-label={t("postcode")}
          autoComplete="postal-code"
          className="h-10 min-w-0 rounded-lg border border-input bg-background px-3 text-base outline-none focus:border-primary sm:text-sm"
          required
        />
        <div className="relative min-w-0">
          <label htmlFor={klusId} className="sr-only">
            {t("typeKlus")}
          </label>
          <select
            id={klusId}
            name="klus"
            value={klus === "" ? klusOpties[0] : klus}
            onChange={(e) => setKlus(e.target.value)}
            aria-label={t("typeKlus")}
            className="h-10 w-full min-w-0 appearance-none rounded-lg border border-input bg-background px-3 pr-10 text-base outline-none focus:border-primary sm:text-sm"
          >
            {klusOpties.map((optie) => (
              <option key={optie} value={optie}>{klusLabel(optie)}</option>
            ))}
          </select>
          <svg
            viewBox="0 0 24 24"
            className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            aria-hidden="true"
          >
            <path d="m6 9 6 6 6-6" />
          </svg>
        </div>
        <button
          type="submit"
          className="h-10 w-full whitespace-nowrap rounded-lg bg-accent px-5 text-sm font-bold text-accent-foreground transition-colors hover:bg-accent-hover sm:w-auto"
        >
          {t("gratisOffertes")}
        </button>
      </div>
      <p className="mt-2 text-left text-xs text-muted-foreground">{t("formVoetnoot")}</p>
    </form>
  );
}

export function Page({
  title,
  intro,
  children,
}: {
  title: string;
  intro: string;
  children: React.ReactNode;
}) {
  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className={`${PAGE_CONTAINER} py-16`}>
        <h1 className="max-w-3xl text-4xl font-extrabold tracking-tight sm:text-5xl">{title}</h1>
        <p className="mt-4 max-w-2xl text-lg text-muted-foreground">{intro}</p>
        {children}
      </main>
      <Footer />
    </div>
  );
}
